import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AppShell from "@/components/layout/AppShell";
import ScorecardDialog from "@/components/PlayHQScorecardDialog";
import LiveScorePeek from "@/components/LiveScorePeek";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { toast } from "sonner";
import { AlertCircle, Calendar, Gauge, Loader2, MapPin, Trophy } from "lucide-react";
import { useOverrides } from "@/hooks/useOverrides";
import EditableText from "@/components/EditableText";
import { OverridesContext, useOverridesCtx, E } from "@/lib/overridesContext";
import {
  filterClubTeams,
  formatGameType,
  formatKickoff,
  formatVenue,
  groupByRound,
  pickLogo,
  scoresByTeam,
  teamsById,
} from "@/lib/playhq";

/**
 * PlayHQ live fixtures view for members.
 *
 * Data flow:
 *   1. Load /playhq/club          → learn our org_id
 *   2. Load /playhq/seasons       → season selector
 *   3. On season change:
 *        - Load /playhq/seasons/{sid}/teams   → filter to club teams,
 *          derive grade list from team.raw.grade
 *        - Auto-select the first grade with a Derwent team present
 *   4. On grade change:
 *        - Load /playhq/grades/{gid}/fixtures → group by round, render
 *        - Load /playhq/grades/{gid}/ladder   → alongside the fixtures
 *
 * Fixtures are always filtered to games involving one of Derwent's teams;
 * the ladder shows every team in the grade for full context.
 */
export default function MemberPlayHQ() {
  // ------------------------------------------------------------------
  // State
  // ------------------------------------------------------------------
  const [orgId, setOrgId] = useState("");
  const [seasons, setSeasons] = useState([]);
  const [seasonId, setSeasonId] = useState("");
  const [teams, setTeams] = useState([]);           // all teams for season
  const [gradeId, setGradeId] = useState("");
  const [fixtures, setFixtures] = useState([]);
  const [ladder, setLadder] = useState(null);

  const [loadingBoot, setLoadingBoot] = useState(true);
  const [loadingSeason, setLoadingSeason] = useState(false);
  const [loadingGrade, setLoadingGrade] = useState(false);

  // Selected game id for the scorecard dialog. Only FINAL / IN_PROGRESS games
  // have a summary to show; upcoming ones just no-op the click.
  const [scorecardGameId, setScorecardGameId] = useState(null);

  // Deep-link: `?live=<gameId>` — used by the global live-match toast so
  // tapping "Watch" opens the scorecard modal right away, even on a fresh
  // navigation. Cleared from the URL after we consume it so refreshing
  // the page doesn't keep reopening it.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const liveId = searchParams.get("live");
    if (liveId) {
      setScorecardGameId(liveId);
      const next = new URLSearchParams(searchParams);
      next.delete("live");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Text overrides plumbed via context so inline <E path=...> wrappers can
  // read/write without prop drilling.
  const overrides = useOverrides();
  const overridesCtx = useMemo(() => ({ get: overrides.get, save: overrides.save, clear: overrides.clear }), [overrides.get, overrides.save, overrides.clear]);

  // ------------------------------------------------------------------
  // Derived views
  // ------------------------------------------------------------------
  // filterClubTeams/teamsById are stable module imports
  const clubTeams = useMemo(() => filterClubTeams(teams, orgId), [teams, orgId]);
  const clubTeamIdSet = useMemo(() => new Set(clubTeams.map((t) => t.playhq_id)), [clubTeams]);
  const teamMap = useMemo(() => teamsById(teams), [teams]);

  // Grades in this season that our club actually plays in. Sorted by
  // grade name for consistency across renders.
  const grades = useMemo(() => {
    const seen = new Map();
    for (const t of clubTeams) {
      const g = t.raw?.grade;
      if (!g?.id || seen.has(g.id)) continue;
      seen.set(g.id, { id: g.id, name: g.name });
    }
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [clubTeams]);

  // Cached team names in this grade for the ladder view.
  const gradeTeams = useMemo(() => {
    if (!gradeId) return [];
    return teams.filter((t) => t.raw?.grade?.id === gradeId);
  }, [teams, gradeId]);

  // Games are always filtered to those involving one of our club's teams.
  // For the current (non-COMPLETED) season, we only surface completed
  // games on the Results page — upcoming/live fixtures live on the
  // Fixtures page instead.
  const selectedSeasonMeta = useMemo(
    () => seasons.find((s) => s.playhq_id === seasonId) || null,
    [seasons, seasonId]
  );
  const isCurrentSeasonSelected = !!(selectedSeasonMeta?.status && selectedSeasonMeta.status !== "COMPLETED");
  const visibleFixtures = useMemo(
    () => fixtures.filter((f) => {
      const isClubGame = (f.teams || []).some((t) => clubTeamIdSet.has(t.id));
      if (!isClubGame) return false;
      if (isCurrentSeasonSelected && f.status !== "FINAL") return false;
      return true;
    }),
    [fixtures, clubTeamIdSet, isCurrentSeasonSelected]
  );

  const groupedFixtures = useMemo(() => groupByRound(visibleFixtures), [visibleFixtures]);

  // Season + grade context threaded into every card's metadata bar.
  const currentSeason = selectedSeasonMeta;
  // Newest non-COMPLETED season across the whole seasons list — LiveScorePeek
  // uses this so members always see live scores even while browsing an
  // older season's results.
  const activeSeasonForLive = useMemo(
    () => seasons.find((s) => s.status && s.status !== "COMPLETED") || null,
    [seasons]
  );
  const currentGradeName = useMemo(
    () => (grades.find((g) => g.id === gradeId) || {}).name || null,
    [grades, gradeId]
  );

  // The ladder is only worth showing when it has at least one team row.
  // Grades early in a season often have an empty ladder — we hide the whole
  // card in that case and let the fixtures list use the full width.
  const hasLadder = useMemo(() => {
    const rows = ladder?.raw?.ladders?.[0]?.teams;
    return Array.isArray(rows) && rows.length > 0;
  }, [ladder]);

  // ------------------------------------------------------------------
  // Boot: load club + seasons
  // ------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingBoot(true);
      try {
        const [clubRes, seasonsRes] = await Promise.all([
          api.get("/playhq/club"),
          api.get("/playhq/seasons"),
        ]);
        if (cancelled) return;
        setOrgId(clubRes.data?.org_id || "");
        const arr = Array.isArray(seasonsRes.data) ? seasonsRes.data : [];
        setSeasons(arr);
        // Pick the newest season that has data. "COMPLETED" is fine — it may
        // still be the most recent one available in the cache.
        if (arr.length > 0) {
          const preferred = arr.find((s) => s.status && s.status !== "COMPLETED") || arr[0];
          setSeasonId(preferred.playhq_id);
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load PlayHQ data");
      } finally {
        if (!cancelled) setLoadingBoot(false);
      }
    })();
    return () => { cancelled = true; };
  }, []); // mount-only; api/toast are stable module imports

  // ------------------------------------------------------------------
  // On season change: load teams
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!seasonId) return;
    let cancelled = false;
    (async () => {
      setLoadingSeason(true);
      try {
        const r = await api.get(`/playhq/seasons/${seasonId}/teams`);
        if (cancelled) return;
        setTeams(Array.isArray(r.data) ? r.data : []);
        setGradeId("");     // reset — the useEffect below will pick the first grade
        setFixtures([]);
        setLadder(null);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load teams for season");
      } finally {
        if (!cancelled) setLoadingSeason(false);
      }
    })();
    return () => { cancelled = true; };
  }, [seasonId]); // api/toast are stable module imports

  // Auto-pick the first grade with a Derwent team once teams load.
  useEffect(() => {
    if (!gradeId && grades.length > 0) {
      setGradeId(grades[0].id);
    }
  }, [grades, gradeId]);

  // ------------------------------------------------------------------
  // On grade change: load fixtures + ladder
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!gradeId) return;
    let cancelled = false;
    (async () => {
      setLoadingGrade(true);
      try {
        const [fRes, lRes] = await Promise.all([
          api.get(`/playhq/grades/${gradeId}/fixtures`),
          api.get(`/playhq/grades/${gradeId}/ladder`).catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        setFixtures(Array.isArray(fRes.data) ? fRes.data : []);
        setLadder(lRes.data || null);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load fixtures");
      } finally {
        if (!cancelled) setLoadingGrade(false);
      }
    })();
    return () => { cancelled = true; };
  }, [gradeId]); // api/toast are stable module imports

  // ------------------------------------------------------------------
  // Handlers
  // ------------------------------------------------------------------
  const isBoot = loadingBoot;
  const isBusy = loadingSeason || loadingGrade;

  // ------------------------------------------------------------------
  // Empty state
  // ------------------------------------------------------------------
  if (!isBoot && seasons.length === 0) {
    return (
      <OverridesContext.Provider value={overridesCtx}>
      <AppShell variant="member" title="Results">
        <div className="space-y-6">
          <Card className="p-8 rounded-2xl text-center max-w-lg mx-auto" data-testid="playhq-empty-state">
          <div className="mx-auto h-12 w-12 rounded-2xl bg-[hsl(var(--secondary))] flex items-center justify-center">
            <AlertCircle className="h-6 w-6 text-muted-foreground" />
          </div>
          <h2 className="mt-4 text-lg font-semibold tracking-tight">
            <E path="page.results.empty.title" defaultValue="No results yet" />
          </h2>
          <p className="mt-2 text-sm text-muted-foreground text-balance">
            <E path="page.results.empty.body" defaultValue="An admin needs to sync seasons before results show up here. Once synced, you'll see live grade fixtures and ladders for every Derwent team." multiline />
          </p>
        </Card>
        </div>
      </AppShell>
      </OverridesContext.Provider>
    );
  }

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  return (
    <OverridesContext.Provider value={overridesCtx}>
    <AppShell variant="member" title="Results">
      <div className="space-y-6">
        {/* ============ Live peek ============ */}
        <LiveScorePeek
          currentSeasonId={activeSeasonForLive?.playhq_id}
          onOpen={setScorecardGameId}
        />

        {/* ============ Selectors ============ */}
        <Card className="p-4 sm:p-5 rounded-2xl" data-testid="playhq-selectors-card">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
                <E path="page.results.filter.season.label" defaultValue="Season" />
              </Label>
              <Select value={seasonId} onValueChange={setSeasonId} disabled={isBoot || seasons.length === 0}>
                <SelectTrigger className="mt-1 h-11 rounded-lg" data-testid="playhq-season-select"><SelectValue placeholder={isBoot ? "Loading…" : "Choose a season"} /></SelectTrigger>
                <SelectContent>
                  {seasons.map((s) => (
                    <SelectItem key={s.playhq_id} value={s.playhq_id} data-testid={`playhq-season-option-${s.playhq_id}`}>
                      {s.name}
                      {s.status && s.status !== "COMPLETED" ? ` · ${s.status.toLowerCase().replace(/_/g, " ")}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
                <E path="page.results.filter.grade.label" defaultValue="Grade" />
                {loadingSeason && <span className="ml-1 text-[10px]">loading…</span>}
              </Label>
              <Select
                value={gradeId}
                onValueChange={setGradeId}
                disabled={loadingSeason || grades.length === 0}
              >
                <SelectTrigger className="mt-1 h-11 rounded-lg" data-testid="playhq-grade-select">
                  <SelectValue placeholder={grades.length === 0 ? "No club grades" : "Choose a grade"} />
                </SelectTrigger>
                <SelectContent>
                  {grades.map((g) => (
                    <SelectItem key={g.id} value={g.id} data-testid={`playhq-grade-option-${g.id}`}>
                      <E path={`grade.${g.id}.name`} defaultValue={g.name} />
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-end gap-3 border-t border-border pt-3">
            <div className="text-xs text-muted-foreground" data-testid="playhq-fixture-count">
              {loadingGrade
                ? "Loading fixtures…"
                : `${visibleFixtures.length} ${isCurrentSeasonSelected ? "result" : "game"}${visibleFixtures.length === 1 ? "" : "s"}`}
            </div>
          </div>
        </Card>

        {/* ============ Fixtures + Ladder ============ */}
        <div className={`grid gap-4 sm:gap-6 ${hasLadder ? "lg:grid-cols-3" : ""}`}>
          {/* Fixtures column — full width when the ladder is hidden, else 2/3 */}
          <div className={hasLadder ? "lg:col-span-2" : ""}>
            {isBusy && groupedFixtures.length === 0 ? (
              <Card className="p-10 rounded-2xl text-center text-sm text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                <div className="mt-2">Loading fixtures…</div>
              </Card>
            ) : groupedFixtures.length === 0 ? (
              <Card className="p-10 rounded-2xl text-center" data-testid="playhq-no-fixtures">
                <div className="mx-auto h-10 w-10 rounded-xl bg-[hsl(var(--secondary))] flex items-center justify-center">
                  <Trophy className="h-5 w-5 text-muted-foreground" />
                </div>
                <div className="mt-3 text-sm font-semibold">
                  {isCurrentSeasonSelected ? (
                    <E path="page.results.no-results.title" defaultValue="No completed games yet" />
                  ) : (
                    <E path="page.results.no-fixtures.title" defaultValue="No fixtures yet" />
                  )}
                </div>
                <p className="mt-1 text-xs text-muted-foreground max-w-xs mx-auto">
                  {isCurrentSeasonSelected ? (
                    <E path="page.results.no-results.body" defaultValue="Results for the current season appear here once games finish. Head to Fixtures for upcoming matches." multiline />
                  ) : (
                    <E path="page.results.no-fixtures.body" defaultValue="No games found for our teams in this grade. Try another grade or season." multiline />
                  )}
                </p>
              </Card>
            ) : (
              <div className="space-y-4">
                {groupedFixtures.map((round) => (
                  <div key={round.name} data-testid={`playhq-round-${round.name.replace(/\s+/g, "-")}`}>
                    <div className="flex items-baseline justify-between mb-2 px-1">
                      <h3 className="text-sm font-semibold tracking-tight text-foreground/80">
                        <E path={`round.${round.name}.name`} defaultValue={round.name} />
                      </h3>
                      <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                        {round.games.length} game{round.games.length === 1 ? "" : "s"}
                      </span>
                    </div>
                    <ul className="space-y-3">
                      {round.games.map((g) => (
                        <FixtureRow
                          key={g.playhq_id}
                          game={g}
                          teamMap={teamMap}
                          clubTeamIdSet={clubTeamIdSet}
                          currentSeason={currentSeason}
                          currentGradeName={currentGradeName}
                          onOpen={setScorecardGameId}
                        />
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Ladder column — only rendered when the grade actually has a
              ladder with at least one team on it. */}
          {hasLadder && (
            <div>
              <LadderCard ladder={ladder} teamMap={teamMap} clubTeamIdSet={clubTeamIdSet} loading={loadingGrade} />
            </div>
          )}
        </div>
      </div>

      {/* Full scorecard modal, opened when a fixture card is clicked */}
      <ScorecardDialog
        gameId={scorecardGameId}
        open={!!scorecardGameId}
        onClose={() => setScorecardGameId(null)}
      />
    </AppShell>
    </OverridesContext.Provider>
  );
}

/**
 * PlayHQ-style fixture card:
 *
 *   ┌───────────────────────────────────────────────────────────────┐
 *   │  Team A   [logo]     10/199     8/201    [logo]   Team B      │
 *   │                        (50)      (50)                          │
 *   ├───────────────────────────────────────────────────────────────┤
 *   │  🕘 Format · 📅 Kickoff · 📍 Venue · 🏆 Competition             │
 *   └───────────────────────────────────────────────────────────────┘
 *
 * The metadata bar wraps naturally on narrow screens; the scoreline reads
 * "wickets/runs (overs)" to match PlayHQ's own game centre notation.
 * Cards involving one of Derwent's teams get a subtle accent background.
 */
function FixtureRow({ game, teamMap, clubTeamIdSet, currentSeason, currentGradeName, onOpen }) {
  const home = (game.teams || []).find((t) => t.isHomeTeam) || game.teams?.[0] || {};
  const away = (game.teams || []).find((t) => !t.isHomeTeam) || game.teams?.[1] || {};
  const scores = scoresByTeam(game.raw);
  const isFinal = game.status === "FINAL";
  const involvesClub = (game.teams || []).some((t) => clubTeamIdSet.has(t.id));

  const format = formatGameType(game.type);
  const kickoff = formatKickoff(game.start_at);
  const venueLabel = formatVenue(game.venue);
  const compLine = [currentSeason?.association_name, currentSeason?.name].filter(Boolean).join(", ");

  // Only games with actual data (FINAL or IN_PROGRESS) are clickable — upcoming
  // games would open an empty scorecard, so we make them non-interactive.
  const isClickable = isFinal || game.status === "IN_PROGRESS";

  return (
    <li
      className={`rounded-2xl border overflow-hidden transition-colors ${
        isClickable ? "cursor-pointer" : ""
      } ${
        involvesClub
          ? "border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/[0.04] hover:border-[hsl(var(--accent))]/60"
          : "border-border hover:border-foreground/25 bg-[hsl(var(--card))]"
      }`}
      onClick={isClickable ? () => onOpen?.(game.playhq_id) : undefined}
      onKeyDown={isClickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen?.(game.playhq_id); } } : undefined}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      data-testid={`playhq-game-${game.playhq_id}`}
    >
      {/* Top-right corner: status badge */}
      <div className="flex items-center justify-end gap-1.5 px-4 pt-3">
        <StatusBadge status={game.status} />
      </div>

      {/* Score row — grid so the two team blocks + two score blocks share axes */}
      <div className="px-4 pb-4 pt-1 grid grid-cols-[1fr_auto_auto_1fr] items-center gap-x-2 sm:gap-x-4">
        <FixtureTeam
          teamRef={home}
          teamMap={teamMap}
          clubTeamIdSet={clubTeamIdSet}
          isFinal={isFinal}
          align="right"
        />
        <FixtureScore innings={scores[home.id]} isWinner={isFinal && home.outcome === "WON"} gameId={game.playhq_id} teamId={home.id} />
        <FixtureScore innings={scores[away.id]} isWinner={isFinal && away.outcome === "WON"} gameId={game.playhq_id} teamId={away.id} />
        <FixtureTeam
          teamRef={away}
          teamMap={teamMap}
          clubTeamIdSet={clubTeamIdSet}
          isFinal={isFinal}
          align="left"
        />
      </div>

      {/* Metadata bar */}
      <div className="border-t border-border/60 bg-[hsl(var(--bg))]/40 px-4 py-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
        {format && (
          <MetaItem icon={Gauge} label={format} testid={`playhq-game-format-${game.playhq_id}`} editablePath={`game.${game.playhq_id}.format`} />
        )}
        <MetaItem icon={Calendar} label={kickoff} testid={`playhq-game-kickoff-${game.playhq_id}`} editablePath={`game.${game.playhq_id}.kickoff`} />
        {venueLabel && (
          <MetaItem icon={MapPin} label={venueLabel} testid={`playhq-game-venue-${game.playhq_id}`} editablePath={`game.${game.playhq_id}.venue`} />
        )}
        {(compLine || currentGradeName) && (
          <MetaItem
            icon={Trophy}
            label={[currentGradeName, compLine].filter(Boolean).join(" · ")}
            testid={`playhq-game-comp-${game.playhq_id}`}
            editablePath={`game.${game.playhq_id}.comp`}
          />
        )}
      </div>
    </li>
  );
}

/**
 * One team's identity block. `align="right"` reads home-team-friendly
 * (name → logo, right-aligned); `align="left"` reads away-team-friendly
 * (logo → name, left-aligned).
 */
function FixtureTeam({ teamRef, teamMap, clubTeamIdSet, isFinal, align = "left" }) {
  const team = teamMap[teamRef?.id];
  const name = team?.name || "TBC";
  const logo = pickLogo(team?.raw);
  const isClub = clubTeamIdSet.has(teamRef?.id);
  const isWinner = isFinal && teamRef?.outcome === "WON";
  const nameCls = `text-sm sm:text-base truncate ${isClub ? "font-semibold" : "font-medium"} ${
    isWinner ? "text-foreground" : "text-foreground/85"
  }`;
  const LogoEl = logo ? (
    <img src={logo} alt="" className="h-9 w-9 sm:h-10 sm:w-10 rounded-full object-cover shrink-0 bg-[hsl(var(--secondary))]" loading="lazy" />
  ) : (
    <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-full bg-[hsl(var(--secondary))] shrink-0" />
  );
  const nameEl = (
    <E
      path={teamRef?.id ? `team.${teamRef.id}.name` : undefined}
      defaultValue={name}
      className={`${nameCls} ${align === "right" ? "text-right" : ""}`}
    />
  );
  return (
    <div className={`flex items-center gap-2 sm:gap-3 min-w-0 ${align === "right" ? "flex-row justify-end" : "flex-row justify-start"}`}>
      {align === "right" && nameEl}
      {LogoEl}
      {align === "left" && nameEl}
    </div>
  );
}

/**
 * Score block. Displays "wkts/runs" as one line with the overs rendered
 * dim beneath. Handles multiple innings (Two-Day / Test) by stacking them.
 */
function FixtureScore({ innings, isWinner, gameId, teamId }) {
  const list = Array.isArray(innings) ? innings : innings ? [innings] : [];
  if (list.length === 0) {
    return <div className="text-center text-lg sm:text-2xl tabular-nums font-mono text-muted-foreground/60">—</div>;
  }
  return (
    <div className={`text-center ${isWinner ? "text-foreground" : "text-foreground/80"}`}>
      {list.map((inn, i) => {
        const w = inn?.wickets;
        const r = inn?.runs;
        const overs = inn?.overs;
        const baseScore = r == null ? "—" : `${w != null ? `${w}/` : ""}${r}`;
        const overText = overs != null ? `(${overs})` : "";
        const scorePath = gameId && teamId ? `game.${gameId}.team.${teamId}.innings.${i}.score` : undefined;
        const oversPath = gameId && teamId ? `game.${gameId}.team.${teamId}.innings.${i}.overs` : undefined;
        return (
          <div key={`innings-${i}`} className={i > 0 ? "mt-1" : ""}>
            <div className={`font-mono tabular-nums text-lg sm:text-2xl leading-none ${isWinner ? "font-semibold" : "font-medium"}`}>
              <E path={scorePath} defaultValue={baseScore} />
            </div>
            {(overs != null) && (
              <div className="mt-0.5 text-[10px] tabular-nums font-mono text-muted-foreground">
                <E path={oversPath} defaultValue={overText} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Small icon + label item used inside the fixture-card metadata bar. */
function MetaItem({ icon: Icon, label, testid, editablePath }) {
  return (
    <span className="inline-flex items-center gap-1.5 min-w-0" data-testid={testid}>
      <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70" />
      <E path={editablePath} defaultValue={label} className="truncate text-foreground/70" />
    </span>
  );
}

function StatusBadge({ status }) {
  const map = {
    FINAL: { cls: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30", label: "Final" },
    UPCOMING: { cls: "bg-blue-500/10 text-blue-600 border-blue-500/30", label: "Upcoming" },
    IN_PROGRESS: { cls: "bg-amber-500/10 text-amber-700 border-amber-500/30", label: "Live" },
    ABANDONED: { cls: "bg-muted text-muted-foreground border-border", label: "Abandoned" },
    FORFEIT: { cls: "bg-muted text-muted-foreground border-border", label: "Forfeit" },
    BYE: { cls: "bg-muted text-muted-foreground border-border", label: "Bye" },
  };
  const cfg = map[status] || { cls: "bg-muted text-muted-foreground border-border", label: (status || "").toLowerCase() || "TBC" };
  return (
    <Badge variant="outline" className={`text-[10px] font-semibold uppercase tracking-wider ${cfg.cls}`}>
      {cfg.label}
    </Badge>
  );
}

/**
 * Compact ladder card. PlayHQ ladders come with 19 columns; we only surface
 * the essentials that fit next to the fixtures list (P, W, L, Pts) plus a
 * "netRunRate" tooltip on the position badge for the top team.
 *
 * Ladders can arrive empty (esp. early in a season) — we show a helpful
 * fallback in that case rather than an empty table.
 */
function LadderCard({ ladder, teamMap, clubTeamIdSet, loading }) {
  const teamsRow = ladder?.raw?.ladders?.[0]?.teams || [];
  const rows = teamsRow.slice(0, 12);
  const gradeIdKey = ladder?.grade_id || "unknown";

  return (
    <Card className="p-4 sm:p-5 rounded-2xl" data-testid="playhq-ladder-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
            <E path="page.results.ladder.eyebrow" defaultValue="Standings" />
          </div>
          <h3 className="text-lg font-semibold tracking-tight">
            <E path="page.results.ladder.title" defaultValue="Ladder" />
          </h3>
        </div>
      </div>
      {loading ? (
        <div className="py-10 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="py-8 text-center border border-dashed border-border rounded-xl">
          <div className="text-sm font-semibold">
            <E path="page.results.ladder.empty.title" defaultValue="No ladder yet" />
          </div>
          <p className="mt-1 text-xs text-muted-foreground max-w-[240px] mx-auto">
            <E path="page.results.ladder.empty.body" defaultValue="The grade's ladder is empty — this typically means it's early in the season." multiline />
          </p>
        </div>
      ) : (
        <div className="mobile-scroll-x -mx-1">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
              <tr>
                <th className="text-left py-1.5 pl-1 font-medium">#</th>
                <th className="text-left py-1.5 font-medium">Team</th>
                <th className="text-right py-1.5 font-medium">P</th>
                <th className="text-right py-1.5 font-medium">W</th>
                <th className="text-right py-1.5 font-medium">L</th>
                <th className="text-right py-1.5 pr-1 font-medium">Pts</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const team = teamMap[row.id] || row;
                const isClub = clubTeamIdSet.has(row.id);
                // Ladder cells come as { key, value }[]; map to a lookup.
                const cells = (row.stats || []).reduce((acc, c) => { acc[c.key] = c.value; return acc; }, {});
                const rowKey = row.id || `pos${i}`;
                return (
                  <tr key={row.id || i} className={isClub ? "bg-[hsl(var(--accent))]/[0.06]" : ""} data-testid={`playhq-ladder-row-${row.id || i}`}>
                    <td className="py-2 pl-1 font-mono text-xs text-muted-foreground">{i + 1}</td>
                    <td className={`py-2 truncate max-w-[140px] ${isClub ? "font-semibold" : ""}`}>
                      <E path={row.id ? `team.${row.id}.name` : undefined} defaultValue={team.name || "—"} />
                    </td>
                    <td className="py-2 text-right tabular-nums">
                      <E path={`ladder.${gradeIdKey}.${rowKey}.played`} defaultValue={String(cells.played ?? "—")} />
                    </td>
                    <td className="py-2 text-right tabular-nums">
                      <E path={`ladder.${gradeIdKey}.${rowKey}.won`} defaultValue={String(cells.won ?? "—")} />
                    </td>
                    <td className="py-2 text-right tabular-nums">
                      <E path={`ladder.${gradeIdKey}.${rowKey}.lost`} defaultValue={String(cells.lost ?? "—")} />
                    </td>
                    <td className="py-2 pr-1 text-right tabular-nums font-semibold">
                      <E path={`ladder.${gradeIdKey}.${rowKey}.pts`} defaultValue={String(cells.competitionPoints ?? "—")} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
